import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Descriptions,
  notification,
  Form,
  Space,
  Row,
  Col,
  Input,
  Typography,
  DatePicker,
  Card,
  Skeleton,
} from 'antd';

import MESSAGES from '../../../enums/Messages';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import CustomButton from '../../../components/CustomButton';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import { useParams, Link, useHistory } from 'react-router-dom';
import {
  getModificationDate,
  patch,
} from '../../../api/module/request_modifications_draft';
import '../Form/style.scss';
import { catchException } from '../../../utils/helpers/helpers';
import ModificationModuleSaveButton from '../../../components/ModificationComponents';

const { Title } = Typography;
const dateFormat = 'YYYY-MM-DD';
const dateFormatText = 'DD MMMM YYYY';

const ModificationChangeTerm = ({ sendPerm }) => {
  var { modification_id } = useParams();
  const [formModifyDate] = Form.useForm();
  const [projectId, setProjectID] = useState(0);
  const [selectedDate, setSelectedDate] = useState('');

  const navigate = useHistory();

  const breadcrumbItems = [
    {
      text: 'Módulo Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}`,
    },
    {
      text: 'Formulario de Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${projectId}/${modification_id}`,
    },

    {
      text: 'Modificacion modulo plazo',
    },
  ];

  const [loadingFormDate, setLoadingFormDate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataInfo, setDataInfo] = useState({});
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isMounted, setIsMounted] = useState(true);

  const getModuleInfo = async (modification_id) => {
    setLoading(true);
    try {
      const response = await getModificationDate(modification_id);
      let resInfo = response.data.info ? response.data.info : {};
      let modification = response.data.modificacion
        ? response.data.modificacion
        : {};
      let fechaMod = modification.fecha_fin_contrato
        ? dayjs(modification.fecha_fin_contrato, 'YYYY-MM-DD')
        : null;

      formModifyDate.setFieldsValue({
        modified_date: fechaMod,
        justification: modification.justificacion,
        name_clause: modification.clausula,
      });
      if (modification.justificacion == null) {
        fechaMod = null;
      }
      setSelectedDate(fechaMod);
      setProjectID(resInfo.proyecto_id);
      setDataInfo(resInfo);
      setHasLoaded(true);
      setLoading(false);
    } catch (error) {
      catchException(error);
      setHasLoaded(false);
    }
  };

  useEffect(() => {
    if (modification_id && isMounted) {
      getModuleInfo(modification_id);
    }

    return () => {
      setIsMounted(false);
    };
  }, [modification_id, isMounted]);

  const disabledDate = (current) => {
    let finalDate = dayjs(dataInfo.fecha_fin_proyecto).format('YYYY-MM-DD');
    let startDate = dayjs(dataInfo.fecha_inicio_contrato).format('YYYY-MM-DD');

    return (
      current && (current < dayjs(startDate) || current > dayjs(finalDate))
    );
  };

  const formatDate = (dateVal, strFormat) => {
    var dateFinal = dateVal ? dateVal : '';
    if (dateFinal !== '') {
      dateFinal = dayjs(dateFinal).format(strFormat);
    }
    return dateFinal;
  };

  const onFinish = async (inputs) => {
    setLoadingFormDate(true);
    const date = inputs.modified_date
      ? formatDate(inputs.modified_date, 'YYYY-MM-DD')
      : '';
    const justification = inputs.justification ? inputs.justification : '';
    const clause = inputs.name_clause ? inputs.name_clause : '';

    const data = {
      modified_date: date,
      justification: justification,
      clause: clause,
    };

    try {
      await patch(modification_id, data);

      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: 'Módulo actualizado correctamente.',
        duration: 2,
      });

      setLoadingFormDate(false);
      setTimeout(() => {
        navigate.push(
          `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo.proyecto_id}/${modification_id}`,
        );
      }, MESSAGES.TIME);
    } catch (error) {
      setLoadingFormDate(false);
      catchException(error);
    }
  };

  const handleDateChange = (date, dateString) => {
    setSelectedDate(dateString);
  };

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <div className="box-title">
        <Title className={'text-uppercase'} level={2}>
          MODIFICACIÓN CONTRACTUAL PLAZO
        </Title>
      </div>
      <Row align="middle" gutter={8}>
        <Col flex="auto">
          {dataInfo && (
            <Space direction={'vertical'} style={{ width: '100%' }}>
              <Skeleton loading={loading} active>
                <Descriptions
                  layout="horizontal"
                  column={2}
                  bordered
                  labelStyle={{ maxWidth: '5rem' }}
                  className="fade-in"
                >
                  <Descriptions.Item label="Contratista / Proveedor" span={2}>
                    <Row align="middle">
                      <Col flex="auto">{dataInfo.contratista_nombre}</Col>
                      <Col flex="0 0">
                        <CustomButton
                          htmlType="button"
                          text={
                            <Link
                              to={`${INTERNAL_LINKS.RESUME_CONTRACT_SEARCH}-detail/${dataInfo.id}/${dataInfo.proyecto_id}/${dataInfo.cargo_id}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Ver históricos contractuales
                            </Link>
                          }
                        />
                      </Col>
                    </Row>
                  </Descriptions.Item>
                  <Descriptions.Item label="Cargo/Rol" span={2}>
                    {dataInfo?.cargo_nombre}
                  </Descriptions.Item>
                </Descriptions>
              </Skeleton>
            </Space>
          )}
          <Space direction={'vertical'} style={{ width: '100%' }}>
            <Form
              form={formModifyDate}
              name="form_modificacion_fecha"
              layout="vertical"
              autoComplete="off"
              onFinish={onFinish}
            >
              <Card
                size={'small'}
                bordered={false}
                title={
                  <Title level={3}>1.Plazo (Disminución o prórroga)</Title>
                }
              >
                {dataInfo && sendPerm && (
                  <Form.Item
                    label="Digite en letras el número de la cláusula del contrato a modificar"
                    name="name_clause"
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el nombre de la cláusula',
                      },
                      {
                        max: 100,
                        message:
                          'El campo debe tener un máximo de 100 caracteres',
                      },
                    ]}
                  >
                    <Input
                      type="text"
                      allowClear
                      placeholder=""
                      name="name_clause"
                    />
                  </Form.Item>
                )}
                <Skeleton loading={loading} active>
                  {dataInfo && (
                    <Descriptions
                      layout="horizontal"
                      column={2}
                      bordered
                      size="small"
                      className="fade-in"
                    >
                      <Descriptions.Item
                        label="Fecha de inicio contrato VIGENTE "
                        style={{ maxWidth: '100px' }}
                      >
                        {formatDate(dataInfo.fecha_inicio_contrato, dateFormat)}
                      </Descriptions.Item>
                      <Descriptions.Item
                        styles={{
                          label: { width: 0, padding: 0 },
                          content: { maxWidth: '100px' },
                        }}
                      >
                        {formatDate(
                          dataInfo.fecha_inicio_contrato,
                          dateFormatText,
                        )}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label="Fecha terminación contrato VIGENTE"
                        style={{ maxWidth: '100px' }}
                      >
                        {formatDate(
                          dataInfo.fecha_fin_contrato_vigente,
                          dateFormat,
                        )}
                      </Descriptions.Item>

                      <Descriptions.Item
                        styles={{
                          label: { width: 0, padding: 0 },
                          content: { maxWidth: '100px' },
                        }}
                      >
                        {formatDate(
                          dataInfo.fecha_fin_contrato_vigente,
                          dateFormatText,
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                </Skeleton>
              </Card>
              <Space direction={'vertical'} style={{ width: '100%' }}>
                <Card
                  size={'middle'}
                  bordered={false}
                  title={<Title level={4}>Nueva fecha de terminación</Title>}
                  className="fade-in"
                >
                  <Row>
                    <Col span={12}>
                      <Form.Item
                        label="Fecha terminación modificada"
                        name="modified_date"
                        rules={[
                          {
                            required: true,
                            type: 'object',
                          },
                        ]}
                      >
                        {dataInfo && hasLoaded && (
                          <DatePicker
                            allowClear
                            onChange={handleDateChange}
                            format={dateFormat}
                            disabledDate={disabledDate}
                            placeholder="Fecha modificada"
                            style={{
                              width: '100%',
                            }}
                          />
                        )}
                      </Form.Item>
                    </Col>
                    <Col span={12} align="middle">
                      <p
                        style={{
                          textAlign: 'left',
                          paddingTop: '35px',
                          paddingLeft: '10px',
                        }}
                      >
                        {formatDate(selectedDate, dateFormatText)}
                      </p>
                    </Col>
                  </Row>
                  <Form.Item
                    label="Justificación"
                    name="justification"
                    rules={[
                      {
                        required: true,
                        message: 'Ingresa la Justificación',
                      },
                    ]}
                  >
                    <Input.TextArea allowClear />
                  </Form.Item>

                  {hasLoaded && (
                    <Space>
                      <CustomButton
                        htmlType="button"
                        className={'back-button'}
                        type="default"
                        text={
                          <Link
                            to={`${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo.proyecto_id}/${modification_id}`}
                          >
                            Volver
                          </Link>
                        }
                      />
                      <ModificationModuleSaveButton
                        loading={loadingFormDate}
                        onConfirm={() => formModifyDate.submit()}
                      />
                    </Space>
                  )}
                </Card>
              </Space>
            </Form>
          </Space>
        </Col>
      </Row>
    </>
  );
};

export default ModificationChangeTerm;
